import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import {
  GET_DUGOUT_ORGANIZATION_QUERY_KEY,
  GET_DUGOUT_ORGANIZATIONS_QUERY_KEY,
  GET_ORGANIZATION_DELETION_BLOCKERS_QUERY_KEY
} from '@/constants/organizations';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import DugoutOrganizationRepository from '@/repositories/DugoutOrganizationRepository';
import type { OrganizationPayload } from '@/types/organization';
import type { QueryParams } from '@/types/query';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

export const useDugoutOrganizations = (params: QueryParams) => {
  const repository = DugoutOrganizationRepository();

  return useAPI({
    queryKey: [GET_DUGOUT_ORGANIZATIONS_QUERY_KEY, params],
    queryFn: async () => await repository.list(params),
    keepPreviousData: true
  });
};

export const useDugoutOrganization = (organizationId: string | undefined) => {
  const repository = DugoutOrganizationRepository();

  return useAPI({
    queryKey: [GET_DUGOUT_ORGANIZATION_QUERY_KEY, organizationId],
    queryFn: async () => await repository.get(organizationId as string),
    enabled: !!organizationId
  });
};

export const useOrganizationDeletionBlockers = (
  organizationId: string | undefined,
  enabled = true
) => {
  const repository = DugoutOrganizationRepository();

  return useAPI({
    queryKey: [GET_ORGANIZATION_DELETION_BLOCKERS_QUERY_KEY, organizationId],
    queryFn: async () => await repository.deletionBlockers(organizationId as string),
    enabled: enabled && !!organizationId
  });
};

export const useUpdateOrganization = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const repository = DugoutOrganizationRepository();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<OrganizationPayload> }) =>
      repository.update(id, data),
    onSuccess: async (_data, { id }) => {
      addMessage({ level: 'success', text: 'Organization updated' });
      await queryClient.invalidateQueries({ queryKey: [GET_DUGOUT_ORGANIZATIONS_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [GET_DUGOUT_ORGANIZATION_QUERY_KEY, id] });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useDeleteOrganization = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const repository = DugoutOrganizationRepository();

  return useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: async (_data, id) => {
      addMessage({ level: 'success', text: 'Organization deleted' });
      // Drop rather than invalidate: refetching would just 404, while the
      // cached copy would keep rendering the deleted org on Back.
      queryClient.removeQueries({ queryKey: [GET_DUGOUT_ORGANIZATION_QUERY_KEY, id] });
      queryClient.removeQueries({
        queryKey: [GET_ORGANIZATION_DELETION_BLOCKERS_QUERY_KEY, id]
      });
      await queryClient.invalidateQueries({ queryKey: [GET_DUGOUT_ORGANIZATIONS_QUERY_KEY] });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};
